import { getBuyerId, getGuestId, getUserType } from 'dibs-cookie-jar';
import { type FC, useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy.ts';
import { CUSTOMER_TYPE_TRADE } from 'dibs-buyer-layout/exports/userStorageConstants.ts';
import { getUserSessionCountryCode } from 'dibs-regional-info/src/regionalInfoHelpers.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { isRecentlyFavoritedPdpVariant } from '../../../utils/isRecentlyFavoritedHelper.ts';
// components
import { SellerReviewInformationLoading } from 'dibs-seller-reviews-modal';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { PdpRespMarketplaceLoadingCarousel } from '../PdpRespMarketplaceLoadingCarousel/PdpRespMarketplaceLoadingCarousel.tsx';
import { SinglePersonalizedCollectionBannerLoading } from 'dibs-personalized-collections/exports/LazySinglePersonalizedCollectionBanner.ts';

// lazy components
const PdpRespMarketplaceLayoutSecondaryLazy = dibsLazy(
    'PdpRespMarketplaceLayoutSecondary',
    () =>
        import(
            /* webpackChunkName: "PdpRespMarketplaceLayoutSecondary" */ './PdpRespMarketplaceLayoutSecondary.tsx'
        )
);

// types
import { type pdpRespMarketplaceSecondaryRootQuery$variables as SecondaryRelayVariables } from '../../../queries/pdpRespMarketplaceSecondaryRoot.ts';

const LoadingCarousel: FC<{
    isPdpSellerReviewsVariant: boolean | undefined;
    isPersonalizedCollectionTestVariant: boolean | undefined;
}> = ({ isPdpSellerReviewsVariant, isPersonalizedCollectionTestVariant }) => {
    return (
        <>
            {isPdpSellerReviewsVariant && <SellerReviewInformationLoading />}
            <PdpRespMarketplaceLoadingCarousel />
            {isPersonalizedCollectionTestVariant && (
                <SinglePersonalizedCollectionBannerLoading isMobile={false} />
            )}
            <PdpRespMarketplaceLoadingCarousel />
            {isPersonalizedCollectionTestVariant && (
                <SinglePersonalizedCollectionBannerLoading isMobile={false} />
            )}
        </>
    );
};

export const PdpRespMarketplaceLayoutSecondaryRenderer: FC<{
    ssr: boolean;
    relayVariables: SecondaryRelayVariables;
}> = ({ ssr, relayVariables: serverRelayVariables }) => {
    const relayVariables = useRef(serverRelayVariables);
    const layoutVisibilityRef = useRef<HTMLDivElement>(null);
    const {
        isBot,
        isPdpSellerReviewsVariant,
        isPdpBottomNewSlModuleTestVariant,
        isPersonalizedCollectionTestVariant,
    } = useServerVarsContext();
    const [shouldRenderAfterDisplay, setShouldRenderAfterDisplay] = useState(false);

    useEffect(() => {
        const userId = getBuyerId(document.cookie) || '';
        const userType = getUserType(document.cookie);
        const guestId = getGuestId(document.cookie) || '';
        const userCountryCode = getUserSessionCountryCode();

        relayVariables.current = {
            ...serverRelayVariables,
            userId,
            guestId,
            userCountryCode,
            afterDisplay: true,
            afterDisplayAndBtNone: !isBot,
            fetchUser: !!userId,
            isTrade: userType === CUSTOMER_TYPE_TRADE,
            fetchSellerReviewsSection: !!isPdpSellerReviewsVariant,
            isRecentlyFavoritedPdpVariant: isRecentlyFavoritedPdpVariant(),
            maxSponsoredItems: !isPdpBottomNewSlModuleTestVariant ? 10 : null,
            minSponsoredItemsSteps: isPdpBottomNewSlModuleTestVariant ? [5, 10, 15] : [5],
        };
    }, [isBot, serverRelayVariables, isPdpSellerReviewsVariant, isPdpBottomNewSlModuleTestVariant]);

    const scrollHandler = useCallback(() => setShouldRenderAfterDisplay(true), []);
    useEffect(() => {
        window.addEventListener('scroll', scrollHandler);
        return () => window.removeEventListener('scroll', scrollHandler);
    }, [scrollHandler]);

    useEffect(() => {
        if (shouldRenderAfterDisplay) {
            window.removeEventListener('scroll', scrollHandler);
        }
    }, [shouldRenderAfterDisplay, scrollHandler]);

    return (
        <>
            <VisibilityTracker
                elementRef={layoutVisibilityRef}
                onVisibilityChange={({ isVisible }) =>
                    isVisible && setShouldRenderAfterDisplay(true)
                }
            />
            <div ref={layoutVisibilityRef} />
            {ssr || shouldRenderAfterDisplay ? (
                <Suspense
                    fallback={
                        <LoadingCarousel
                            isPdpSellerReviewsVariant={isPdpSellerReviewsVariant}
                            isPersonalizedCollectionTestVariant={
                                isPersonalizedCollectionTestVariant
                            }
                        />
                    }
                >
                    <PdpRespMarketplaceLayoutSecondaryLazy
                        relayVariables={relayVariables.current}
                        afterDisplay={shouldRenderAfterDisplay}
                    />
                </Suspense>
            ) : (
                <LoadingCarousel
                    isPdpSellerReviewsVariant={isPdpSellerReviewsVariant}
                    isPersonalizedCollectionTestVariant={isPersonalizedCollectionTestVariant}
                />
            )}
        </>
    );
};
